import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { EmploymentTypes } from '@app/constants/employment-types';
import { isSameModel } from '@app/functions';
import { Account } from '@app/models/account/account.model';
import { Company } from '@app/models/company/company.model';
import { Employee } from '@app/models/employee/employee.model';
import { Salary } from '@app/models/employee/salary.model';
import { OnboardingStatusService } from '@app/modules/self-serve/services/onboarding-status.service';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { createNumberMask } from 'text-mask-addons';
import { EmploymentTypeDisplayOptions } from '@app/types/translatables/employment-type-display.options';
import { AvatarUploadEvent } from '@app/components/avatar-upload/avatar-upload.component';
import { Address } from '@app/models/employee/address.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Office } from '@models/company/office.model';
import { Job } from '@models/company/job.model';
import { Department } from '@models/company/department.model';
import { stripNonNumeric } from '@app/functions/strip-non-numeric';
import { resetCursorPosition } from '@app/functions/reset-cursor-position';
import { filter } from 'rxjs/operators';

@Component({
    templateUrl: './complete-your-profile.template.html',
    styleUrls: ['../../../self-serve.style.scss'],
})
export class CompleteYourProfileView implements OnInit {
    @ViewChild('form', { static: true }) form!: NgForm;
    isLoading = true;
    isSaving = false;
    company: Company = new Company();
    employee: Employee = new Employee();
    employeeAddress: Address = new Address();
    account: Account = new Account();
    offices: Office[] = [];
    jobs: Job[] = [];
    newJob = new Job();
    departments: Department[] = [];
    newDepartment = new Department();
    salary: Salary = new Salary({ frequency: 'year', name: 'Regular Pay', isPrimary: true });
    currencyMask = createNumberMask({
        prefix: '$',
        includeThousandsSeparator: true,
        allowDecimal: true,
        guide: true,
        keepCharPositions: false,
    });
    showIWillDoItLater = false;
    isAvatarDialogOpen = false;
    isSameModel = isSameModel;
    showCreateJobModal = false;
    showCreateDepartmentModal = false;

    readonly employmentTypeValues: string[] = EmploymentTypes;
    readonly employmentTypeDisplay = EmploymentTypeDisplayOptions;

    private nextPage = 'add-employees';

    constructor(
        private analyticService: AnalyticService,
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthService,
        private onboardingStatusService: OnboardingStatusService,
        private notifyService: NotifyService
    ) {
        this.onboardingStatusService.onboardingStatus$
            .pipe(
                filter((onboardingStatus) => onboardingStatus !== null),
                takeUntilDestroyed()
            )
            .subscribe((onboardingStatus) => {
                this.showIWillDoItLater = !onboardingStatus?.addYourTeam.steps.completeYourProfile;
            });
    }

    async ngOnInit(): Promise<void> {
        try {
            await this.loadData();
        } catch (error) {
            this.notifyService.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    async onSubmit(): Promise<void> {
        if (!this.form.valid || this.isSaving) {
            return;
        }

        try {
            this.isSaving = true;
            await Promise.all([
                this.employee.save(),
                this.employee.account.save(),
                this.salary.save(),
                this.employeeAddress.save(),
            ]);

            this.onboardingStatusService.refresh();

            this.analyticService.trackEvent(AnalyticEvents.SelfServeMyProfileClickSave);

            this.next();
        } catch (error) {
            this.notifyService.error(error);
            this.isSaving = false;
        }
    }

    handleAvatarUploadTerminated({ isSuccessful }: AvatarUploadEvent): void {
        this.isAvatarDialogOpen = false;
        if (isSuccessful) {
            this.notifyService.success('account.avatarUpdated');
            return;
        }

        this.notifyService.error('account.avatarUpdatedError');
    }

    next(): void {
        this.analyticService.trackEvent(AnalyticEvents.SelfServeMyProfileClickLater);
        this.router.navigate(['..', this.nextPage], { relativeTo: this.route });
    }

    clearPosition(): void {
        this.newJob = new Job();
        this.showCreateJobModal = false;
    }

    clearDepartment(): void {
        this.newDepartment = new Department();
        this.showCreateDepartmentModal = false;
    }

    stripNonNumeric(val: string | number): number {
        if (!val) {
            return 0;
        }

        return stripNonNumeric(val) ?? 0;
    }

    resetCursorPosition(): void {
        resetCursorPosition();
    }

    async onCreatePosition(job: Job): Promise<void> {
        try {
            await job.param('company', this.authService.company.id).save();
            this.jobs.push(job);
            this.employee.job = job;
            this.jobs.sort((a: Job, b: Job) => a.title.localeCompare(b.title));
        } catch (error) {
            this.notifyService.error(error);
        } finally {
            this.clearPosition();
        }
    }

    async onCreateDepartment(department: Department): Promise<void> {
        try {
            await department.param('company', this.authService.company.id).save();
            this.departments.push(department);
            this.employee.department = department;
            this.departments.sort((a: Department, b: Department) => a.name.localeCompare(b.name));
        } catch (error) {
            this.notifyService.error(error);
        } finally {
            this.clearDepartment();
        }
    }

    openAvatarDialog(): void {
        this.isAvatarDialogOpen = true;
        this.analyticService.trackEvent(AnalyticEvents.SelfServeMyProfileClickUploadAvatar);
    }

    private async loadData(): Promise<void> {
        const [company, employee, [salaries], [offices], [jobs], [departments]] = await Promise.all([
            Company.find(this.authService.company.id),
            Employee.param('company', this.authService.company.id)
                .with(['account', 'job', 'department', 'address'])
                .find(this.authService.employee.id),
            Salary.param('company', this.authService.company.id)
                .param('employee', this.authService.employee.id)
                .where('current', true)
                .get(),
            Office.param('company', this.authService.company.id).all(),
            Job.param('company', this.authService.company.id).orderBy('title', 'ASC').all(),
            Department.param('company', this.authService.company.id).orderBy('name', 'ASC').all(),
        ]);

        this.company = company;
        this.employee = employee;
        this.employee.currency = 'CAD';
        this.account = this.employee.account;
        this.jobs = jobs;
        this.offices = offices;
        this.departments = departments;

        // if they have already created an address, use it
        if (this.employee.address) {
            this.employeeAddress = this.employee.address;
        }

        // if the employee has an existing salary, use it.
        if (salaries.length) {
            this.salary = salaries[0];
        }

        // new salary
        if (!this.salary.effectiveAt) {
            this.salary.effectiveAt = this.employee.hiredAt ? new Date(this.employee.hiredAt) : new Date();
            this.salary.employeeId = this.employee.id;
        }

        // set default office if there is only one
        if (!this.employee.officeId && offices.length === 1) {
            this.employee.officeId = offices[0].id;
        }

        // set required params before saving
        this.salary.param('company', this.company.id).param('employee', this.authService.employee.id);
        this.employeeAddress.param('company', this.authService.company.id).param('employee', this.employee.id);
    }
}
